/* ============================================================
   PriceChart — Lightweight Charts (TradingView) candlestick chart
   for the DetailPage.
   ------------------------------------------------------------
   - Six timeframes: 1D, 5D, 1M, 6M, YTD, 1Y
       - 1D/5D  → intraday bars from the Worker /candles endpoint,
                  server-routed Schwab-or-FMP via the intraday
                  dispatcher (per-user, per active connection)
       - 1M/6M/YTD/1Y → daily bars (FMP)
   - Extended-hours differentiation (intraday only):
       - Per-bar candle color: RTH = full green/red, pre/after =
         same hue at ~50% opacity
       - Background stripes: overlay canvas painted with a faint
         blue tint under pre-market bars and a faint purple tint
         under after-hours bars; redrawn on visible-range change
         so pan/zoom stay in sync
   - Overlays as horizontal priceLines:
       - S / R levels from calc.supportResistance (S dashed, R
         dashed, both amber)
       - Range HI / Range LO of the visible period (dotted, mute)
   - Source badge in the header — green if Schwab (real-time),
     mute if FMP (~15 min delayed)
   ============================================================ */

import { useEffect, useMemo, useRef, useState } from "react";
import { createChart, LineStyle, CrosshairMode } from "lightweight-charts";
import { api } from "../lib/api.js";

// Palette — kept in-file so the chart can render standalone without
// pulling DetailPage's C.* palette. Values are the same subset.
const C = {
  bg:        "#0A1428",
  panel:     "#132038",
  border:    "#1F3355",
  divider:   "#16273F",
  text:      "#D5DEE9",
  mute:      "#8194AE",
  dim:       "#54709A",
  amber:     "#E8B339",
  green:     "#26D07C",
  red:       "#DC2626",   // was #FF5C5C — bumped to the same deeper red used
                          // by the LO 1D axis tag so the whole down-move
                          // palette (candles + tag) reads consistent
  // 50% opacity extended-hours candle colors — hex-with-alpha
  greenXH:   "#26D07C80",
  redXH:     "#DC262680",  // 50% of the new C.red
  // Background tints for pre-market / after-hours strips. Opacity is
  // deliberate: strong enough to be a clear visual band, weak enough
  // that the candles inside the band still read clearly.
  bandPre:   "rgba(59, 130, 246, 0.18)",   // cool blue
  bandAft:   "rgba(168,  85, 247, 0.18)",  // cool purple
};

const RANGES = ["1D", "5D", "1M", "6M", "YTD", "1Y"];

export default function PriceChart({ symbol }) {
  const containerRef  = useRef(null);
  const overlayRef    = useRef(null);
  const chartRef      = useRef(null);
  const seriesRef     = useRef(null);
  const priceLinesRef = useRef([]);

  const [range,   setRange]   = useState("1D");
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  /* -----------------------------------------------------------
     Chart init — runs once per mount. All chart mutations after
     this go through refs; the chart itself is never re-created
     on data change.
  ----------------------------------------------------------- */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const chart = createChart(el, {
      layout: {
        // Transparent so the overlay-canvas background stripes
        // (positioned BEHIND the chart) show through the pane.
        background: { type: "solid", color: "rgba(0,0,0,0)" },
        textColor:  C.text,
        fontSize:   11,
        fontFamily: "inherit",
      },
      grid: {
        vertLines: { color: C.divider },
        horzLines: { color: C.divider },
      },
      rightPriceScale: {
        borderColor:  C.border,
        scaleMargins: { top: 0.08, bottom: 0.08 },
      },
      timeScale: {
        borderColor:    C.border,
        timeVisible:    true,
        secondsVisible: false,
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { color: C.dim, width: 1, style: LineStyle.Dashed, labelBackgroundColor: C.amber },
        horzLine: { color: C.dim, width: 1, style: LineStyle.Dashed, labelBackgroundColor: C.amber },
      },
      autoSize: true,
    });

    const series = chart.addCandlestickSeries({
      upColor:         C.green,
      downColor:       C.red,
      borderUpColor:   C.green,
      borderDownColor: C.red,
      wickUpColor:     C.green,
      wickDownColor:   C.red,
    });

    chartRef.current  = chart;
    seriesRef.current = series;

    return () => {
      chart.remove();
      chartRef.current  = null;
      seriesRef.current = null;
      priceLinesRef.current = [];
    };
  }, []);

  /* -----------------------------------------------------------
     Data fetch on (symbol, range) change.
  ----------------------------------------------------------- */
  useEffect(() => {
    let stopped = false;
    setLoading(true);
    setError(null);
    api.candles(symbol, range)
      .then(d => {
        if (stopped) return;
        setData(d);
        setLoading(false);
      })
      .catch(e => {
        if (stopped) return;
        setError((e && e.message) || "fetch_failed");
        setLoading(false);
      });
    return () => { stopped = true; };
  }, [symbol, range]);

  /* -----------------------------------------------------------
     Bars → LWC. Per-bar color for extended-hours differentiation.
     Also (re)draws S/R + range HI/LO price lines.
  ----------------------------------------------------------- */
  useEffect(() => {
    if (!data || !seriesRef.current || !chartRef.current) return;
    const s = seriesRef.current;
    const isIntraday = data.kind === "intraday";

    const lwcBars = data.bars.map(b => {
      const bar = { time: b.t, open: b.o, high: b.h, low: b.l, close: b.c };
      if (isIntraday && b.session !== "rth") {
        const up = b.c >= b.o;
        const col = up ? C.greenXH : C.redXH;
        bar.color       = col;
        bar.borderColor = col;
        bar.wickColor   = col;
      }
      return bar;
    });
    s.setData(lwcBars);

    // Clear then reapply price lines. Order in the array doesn't matter —
    // the priceLines API is just a list of horizontal overlay markers.
    priceLinesRef.current.forEach(pl => s.removePriceLine(pl));
    priceLinesRef.current = [];

    // extra: any additional PriceLineOptions to merge in — used by HI/LO
    // to override the right-margin axis tag's background + text color.
    const addLine = (price, color, style, title, extra) => {
      if (!Number.isFinite(price)) return;
      priceLinesRef.current.push(s.createPriceLine({
        price,
        color,
        lineWidth: 1,
        lineStyle: style,
        axisLabelVisible: true,
        title,
        ...(extra || {}),
      }));
    };
    addLine(data.sr && data.sr.support,    C.amber, LineStyle.Dashed, "S");
    addLine(data.sr && data.sr.resistance, C.amber, LineStyle.Dashed, "R");
    // HI 1D — GREEN axis tag, white text (day's high visually reads as bullish).
    // LO 1D — RED   axis tag, white text (day's low  visually reads as bearish).
    // axisLabelColor sets the tag BACKGROUND on LWC v4; axisLabelTextColor
    // sets the text inside. `color` (the line color) stays C.mute so the
    // dotted line itself remains subtle — only the tag pops.
    addLine(data.rangeHigh, C.mute, LineStyle.Dotted, `HI ${data.range}`, {
      axisLabelColor:     C.green,
      axisLabelTextColor: "#000000", // white-on-bright-green was too low contrast; black on green reads clean
    });
    addLine(data.rangeLow,  C.mute, LineStyle.Dotted, `LO ${data.range}`, {
      // C.red (#FF5C5C) reads salmon/orange on a small rounded pill next to
      // the bright green HI tag. Bumping to a deeper saturated red so it
      // reads unambiguously as "red" at pill scale, matching the visual
      // weight of the down candle bodies.
      axisLabelColor:     "#DC2626",
      axisLabelTextColor: "#FFFFFF",
    });

    // Snap the visible range to the data on each fresh load — matches
    // TradingView's behavior when switching timeframes.
    chartRef.current.timeScale().fitContent();
  }, [data]);

  /* -----------------------------------------------------------
     Extended-hours background bands — overlay canvas positioned
     BEHIND the chart pane (chart bg is transparent). Grouped
     into contiguous same-session spans so we paint one rect per
     span instead of one per bar.
  ----------------------------------------------------------- */
  const bandSpans = useMemo(() => {
    if (!data || data.kind !== "intraday" || !Array.isArray(data.bars)) return [];
    const spans = [];
    let cur = null;
    let start = null;
    let prev = null;
    for (const b of data.bars) {
      if (b.session !== cur) {
        if (cur && cur !== "rth" && prev != null) {
          spans.push({ session: cur, from: start, to: prev });
        }
        cur = b.session;
        start = b.t;
      }
      prev = b.t;
    }
    if (cur && cur !== "rth" && prev != null) {
      spans.push({ session: cur, from: start, to: prev });
    }
    return spans;
  }, [data]);

  useEffect(() => {
    if (!chartRef.current || !overlayRef.current) return;
    const canvas = overlayRef.current;
    const chart  = chartRef.current;
    // Capture bars from data for the dynamic-barSpacing probe below.
    // Undefined-safe (daily ranges have no session tags and land here
    // with bandSpans=[] so we'd bail out anyway).
    const bars = (data && Array.isArray(data.bars)) ? data.bars : [];

    /* ---- Derive ACTUAL current pixel spacing between bars.
       ts.options().barSpacing returns the CONFIGURED value (LWC default:
       6). After timeScale.fitContent() the rendered spacing on 1D 5-min
       is closer to 15-25 px. Using the config value under-sizes each
       band by ~4x, leaving visible slivers instead of full-column bands.
       Sample pixel deltas between 6 pairs of consecutive bars and take
       the median — robust against a single null return from
       timeToCoordinate at the boundaries. ---- */
    const currentBarWidth = () => {
      const ts = chart.timeScale();
      const samples = [];
      const step = Math.max(1, Math.floor(bars.length / 12));
      for (let i = step; i < bars.length; i += step) {
        const p1 = ts.timeToCoordinate(bars[i - step].t);
        const p2 = ts.timeToCoordinate(bars[i].t);
        if (Number.isFinite(p1) && Number.isFinite(p2)) {
          const per = Math.abs(p2 - p1) / step; // pixels per single-bar-step
          if (per > 0) samples.push(per);
        }
        if (samples.length >= 6) break;
      }
      if (samples.length === 0) return 8; // fallback matches old default
      samples.sort((a, z) => a - z);
      return samples[Math.floor(samples.length / 2)];
    };

    const redraw = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr  = window.devicePixelRatio || 1;
      canvas.width  = Math.max(1, Math.floor(rect.width  * dpr));
      canvas.height = Math.max(1, Math.floor(rect.height * dpr));
      const ctx = canvas.getContext("2d");
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, rect.width, rect.height);

      const ts = chart.timeScale();

      /* ---- 1. Extended-hours background bands (intraday only). ---- */
      if (bandSpans.length > 0) {
        const barW = currentBarWidth();
        const halfBarW = barW / 2;
        for (const span of bandSpans) {
          let x1 = ts.timeToCoordinate(span.from);
          let x2 = ts.timeToCoordinate(span.to);
          if (x1 == null && x2 == null) continue;
          if (x1 == null) x1 = 0;
          if (x2 == null) x2 = rect.width;
          x1 -= halfBarW;
          x2 += halfBarW;
          if (x2 < 0 || x1 > rect.width) continue;
          ctx.fillStyle = span.session === "premarket" ? C.bandPre : C.bandAft;
          ctx.fillRect(x1, 0, x2 - x1, rect.height);
        }
      }

      /* ---- 2. S/R word labels on the chart pane.

         Anchored to the RIGHT side, just to the LEFT of the price axis,
         so they sit next to the "S 21.89" / "R 21.89" price tags — the
         reader's eye reads "RESISTANCE" then the axis tag in one sweep.
         Old placement (left edge, 14px in) collided with LWC's TradingView
         watermark that sits in the bottom-left corner: "SUPPORT" rendered
         as "PORT" with the logo occluding "SUP". Same corner risk for a
         "RESISTANCE" that lands top-left.

         `chart.priceScale('right').width()` returns the actual current
         width of the price axis, so the label anchors correctly even if
         the axis widens for longer prices (e.g. BRK.A at 5-digit values).
         Falls back to 60px if the API returns 0/undefined during the
         first paint before the axis has measured itself.

         `priceToCoordinate` returns null when the price is outside the
         current visible range — safe-guard the fillText calls. Runs on
         every timeframe (bandSpans early-return moved so this always fires
         even on daily ranges where session bands don't apply). ---- */
      const series = seriesRef.current;
      if (data && data.sr && series) {
        ctx.font = "bold 11px system-ui, -apple-system, 'Segoe UI', sans-serif";
        ctx.fillStyle = "rgba(232, 179, 57, 0.92)"; // amber, matches the S/R line color
        ctx.textAlign = "left";
        const labelX = 8; // 8px inset from left edge of the pane

        // Both labels anchor to the LEFT because the RIGHT axis is already
        // packed with S / R / HI / LO / current-price tags — no room for
        // word labels there without visual overload. Left side of the pane
        // is empty except for LWC's TradingView watermark in the bottom-
        // left corner, which SUPPORT can collide with when the support
        // level is near the pane's bottom edge. Handle that specifically
        // below; RESISTANCE at the top has no watermark to worry about.

        const yR = Number.isFinite(data.sr.resistance) ? series.priceToCoordinate(data.sr.resistance) : null;
        const yS = Number.isFinite(data.sr.support)    ? series.priceToCoordinate(data.sr.support)    : null;

        if (Number.isFinite(yR)) {
          ctx.textBaseline = "alphabetic";
          ctx.fillText("RESISTANCE", labelX, yR - 4);
        }

        if (Number.isFinite(yS)) {
          // TradingView watermark sits bottom-left, roughly the bottom
          // 45px × 130px of the pane. If SUPPORT's default "below the S
          // line" placement would land its text (11px tall + 4px padding
          // = ~15px total below the line) inside that zone, flip it to
          // ABOVE the S line instead so the label still labels the right
          // level without being occluded by the logo. The label stays on
          // the LEFT (x=8) either way — flip is vertical only.
          const WATERMARK_ZONE_H = 45;
          const supportBelowWouldHitWatermark = (yS + 15) > (rect.height - WATERMARK_ZONE_H);
          if (supportBelowWouldHitWatermark) {
            ctx.textBaseline = "alphabetic";
            ctx.fillText("SUPPORT", labelX, yS - 4);   // above the S line
          } else {
            ctx.textBaseline = "top";
            ctx.fillText("SUPPORT", labelX, yS + 4);   // default: below the S line
          }
        }
      }
    };

    // Defer first paint one frame so LWC has settled after fitContent().
    // Without this, the first redraw runs before the timeScale reflects
    // the fitted range and timeToCoordinate returns stale positions.
    const rafId = requestAnimationFrame(redraw);
    const ts = chart.timeScale();
    const unsub = ts.subscribeVisibleTimeRangeChange(redraw);
    const ro = new ResizeObserver(redraw);
    ro.observe(canvas);
    return () => {
      cancelAnimationFrame(rafId);
      unsub && unsub();
      ro.disconnect();
    };
  }, [bandSpans, data]);

  return (
    <div>
      {/* Header: timeframe pills + source badge */}
      <div style={{
        display:        "flex",
        alignItems:     "center",
        justifyContent: "space-between",
        gap:            8,
        marginBottom:   12,
        flexWrap:       "wrap",
      }}>
        <div style={{
          display:      "flex",
          border:       `1px solid ${C.border}`,
          borderRadius: 6,
          overflow:     "hidden",
          flexShrink:   0,
        }}>
          {RANGES.map(r => (
            <button
              key={r}
              onClick={() => setRange(r)}
              style={{
                padding:    "4px 10px",
                fontSize:   11,
                fontWeight: range === r ? 700 : 400,
                border:     "none",
                cursor:     "pointer",
                background: range === r ? C.amber : "transparent",
                color:      range === r ? C.bg    : C.mute,
                fontFamily: "inherit",
              }}
            >
              {r}
            </button>
          ))}
        </div>
        <div style={{
          fontSize:       10,
          letterSpacing:  1,
          textTransform:  "uppercase",
          color:          data && data.realtime ? C.green : C.mute,
          fontWeight:     600,
          fontVariantNumeric: "tabular-nums",
        }}>
          {data ? data.label : "loading…"}
        </div>
      </div>

      {/* Chart pane — overlay canvas underneath, chart transparent on top */}
      <div style={{
        position:     "relative",
        height:       360,
        background:   C.panel,
        border:       `1px solid ${C.border}`,
        borderRadius: 8,
        overflow:     "hidden",
      }}>
        <canvas
          ref={overlayRef}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 0 }}
        />
        <div
          ref={containerRef}
          style={{ position: "absolute", inset: 0, zIndex: 1 }}
        />
        {(loading || error) && (
          <div style={{
            position:       "absolute",
            inset:          0,
            display:        "flex",
            alignItems:     "center",
            justifyContent: "center",
            color:          error ? C.red : C.mute,
            fontSize:       12,
            pointerEvents:  "none",
            background:     "rgba(19,32,56,0.55)",
            zIndex:         2,
          }}>
            {error ? `Chart error: ${error}` : "Loading…"}
          </div>
        )}
      </div>

      {/* Legend — only meaningful on intraday ranges (session differentiation) */}
      {data && data.kind === "intraday" && (
        <div style={{
          display:       "flex",
          gap:           14,
          fontSize:      10,
          color:         C.mute,
          marginTop:     8,
          textTransform: "uppercase",
          letterSpacing: 0.5,
          flexWrap:      "wrap",
        }}>
          <Swatch color={C.green} label="RTH" solid />
          <Swatch color={C.greenXH} label="Pre / after" solid />
          <Swatch color={C.bandPre} label="Pre-market" solid />
          <Swatch color={C.bandAft} label="After-hours" solid />
          <Swatch color={C.amber} label="S / R" solid={false} dashed />
          <Swatch color={C.mute}  label={`HI / LO ${range}`} solid={false} dotted />
        </div>
      )}
    </div>
  );
}

function Swatch({ color, label, solid, dashed, dotted }) {
  const style = solid
    ? { background: color }
    : { borderTop: `1.5px ${dashed ? "dashed" : dotted ? "dotted" : "solid"} ${color}` };
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
      <span style={{
        display: "inline-block",
        width:   14,
        height:  solid ? 8 : 2,
        ...style,
      }} />
      <span>{label}</span>
    </span>
  );
}
